import {
    Position,
    positionKey,
    parsePositionKey,
} from '../core/GridGeometry.js';
import { GridSerializer } from '../core/GridSerialization.js';

export class VortexGateManager {
    private vortexGatePairs: Array<[Position, Position]> = [];
    private vortexAllGates: Set<string> = new Set();
    private activeGatePairs: Map<string, string> = new Map(); // bidirectional posKey→pairedPosKey

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(
        gatePairs: Array<[Position, Position]>,
        allGates: Set<string>,
        onChanged?: () => void
    ): void {
        this.vortexGatePairs = gatePairs;
        this.vortexAllGates = allGates;

        // Build Hub pair map for validation
        const hubPairMap = new Map<string, string>();
        for (const [a, b] of gatePairs) {
            hubPairMap.set(positionKey(a), positionKey(b));
            hubPairMap.set(positionKey(b), positionKey(a));
        }

        // Remove active pairs that are no longer valid in the Hub
        const toRemove: string[] = [];
        for (const [key, pairedKey] of this.activeGatePairs) {
            if (!allGates.has(key) || !allGates.has(pairedKey) || hubPairMap.get(key) !== pairedKey) {
                toRemove.push(key);
            }
        }
        for (const key of toRemove) {
            this.activeGatePairs.delete(key);
        }

        if (onChanged) onChanged();
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.vortexAllGates.has(positionKey(pos));
    }

    isGateCellByKey(key: string): boolean {
        return this.vortexAllGates.has(key);
    }

    isActiveGate(pos: Position): boolean {
        return this.activeGatePairs.has(positionKey(pos));
    }

    getActiveGatePairs(): Map<string, string> {
        return new Map(this.activeGatePairs);
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.vortexGatePairs;
    }

    getAllGatePositions(): Set<string> {
        return new Set(this.vortexAllGates);
    }

    /** Direct access to the live activeGatePairs map (for teleportation logic). */
    get raw(): Map<string, string> {
        return this.activeGatePairs;
    }

    hasActiveGates(): boolean {
        return this.activeGatePairs.size > 0;
    }

    /**
     * Count cells in a set, collapsing active gate-paired cells into one.
     * If both cells of a gate pair are in the set, they count as a single cell.
     */
    deduplicateCellCount(cells: Set<string>): number {
        const canonical = new Set<string>();
        for (const key of cells) {
            const pairedKey = this.activeGatePairs.get(key);
            if (pairedKey && cells.has(pairedKey)) {
                canonical.add(key < pairedKey ? key : pairedKey);
            } else {
                canonical.add(key);
            }
        }
        return canonical.size;
    }

    // ── Gate Pair Lookup ──────────────────────────────────────────

    /**
     * Find the paired position for a given gate position key.
     */
    findPair(key: string): { pairedKey: string; pairedPos: Position } | null {
        for (const [a, b] of this.vortexGatePairs) {
            const aKey = positionKey(a);
            const bKey = positionKey(b);
            if (aKey === key) return { pairedKey: bKey, pairedPos: b };
            if (bKey === key) return { pairedKey: aKey, pairedPos: a };
        }
        return null;
    }

    // ── Gate Activation ──────────────────────────────────────────

    /**
     * Check if a gate can be activated.
     * @param gameCheck Optional game-specific validation callback.
     *                  Receives the position and its paired position.
     *                  Return false to block activation.
     */
    canActivateGate(
        pos: Position,
        gameCheck?: (pos: Position, pairedPos: Position) => boolean
    ): boolean {
        const key = positionKey(pos);
        if (!this.vortexAllGates.has(key)) return false;
        if (this.activeGatePairs.has(key)) return false;

        const pair = this.findPair(key);
        if (!pair) return false;

        if (this.activeGatePairs.has(pair.pairedKey)) return false;

        if (gameCheck && !gameCheck(pos, pair.pairedPos)) return false;

        return true;
    }

    /**
     * Toggle a gate pair on or off.
     * @param canActivate Result of canActivateGate() (pre-computed by caller).
     * @param onToggled Optional callback after activation/deactivation.
     */
    toggleVortexGate(
        pos: Position,
        canActivate: boolean,
        onToggled?: () => void
    ): boolean {
        const key = positionKey(pos);

        if (this.activeGatePairs.has(key)) {
            const pairedKey = this.activeGatePairs.get(key)!;
            this.activeGatePairs.delete(key);
            this.activeGatePairs.delete(pairedKey);
            if (onToggled) onToggled();
            return true;
        }

        if (canActivate) {
            const pair = this.findPair(key);
            if (pair) {
                this.activeGatePairs.set(key, pair.pairedKey);
                this.activeGatePairs.set(pair.pairedKey, key);
                if (onToggled) onToggled();
                return true;
            }
        }
        return false;
    }

    // ── State Management ──────────────────────────────────────────

    clearActiveGates(): void {
        this.activeGatePairs.clear();
    }

    // ── Serialization ─────────────────────────────────────────────

    serializeWithBase(basePart: string, gs: GridSerializer): string {
        if (this.activeGatePairs.size === 0) return basePart;
        return basePart + '|' + this.serializeGates(gs);
    }

    serializeGates(gs: GridSerializer): string {
        const serializedPairs = new Set<string>();
        const gateParts: string[] = [];
        for (const [key, pairedKey] of this.activeGatePairs) {
            const pairId = [key, pairedKey].sort().join('|');
            if (serializedPairs.has(pairId)) continue;
            serializedPairs.add(pairId);

            const posA = parsePositionKey(key);
            const posB = parsePositionKey(pairedKey);
            // Compact: two 2-char positions, no separator (gates are always paired).
            gateParts.push(`${gs.serializePosition(posA)}${gs.serializePosition(posB)}`);
        }
        return gateParts.join('');
    }

    /**
     * Parse a gate part into 2-char position-string pairs. Tolerant of the old
     * separated forms ("A1=B7,C3=D4", Knight's "A1>B7") and the compact form
     * ("A1B7C3D4") — separators are stripped, then 4-char chunks are split in two.
     */
    static parseGatePairs(gatePart: string): Array<[string, string]> {
        const compact = gatePart.replace(/[,=>\-]/g, '');
        const pairs: Array<[string, string]> = [];
        for (let i = 0; i + 4 <= compact.length; i += 4) {
            pairs.push([compact.slice(i, i + 2), compact.slice(i + 2, i + 4)]);
        }
        return pairs;
    }

    deserializeGates(gatePart: string, gs: GridSerializer): void {
        for (const [strA, strB] of VortexGateManager.parseGatePairs(gatePart)) {
            try {
                const keyA = positionKey(gs.deserializePosition(strA));
                const keyB = positionKey(gs.deserializePosition(strB));
                if (this.vortexAllGates.has(keyA) && this.vortexAllGates.has(keyB)) {
                    this.activeGatePairs.set(keyA, keyB);
                    this.activeGatePairs.set(keyB, keyA);
                }
            } catch {
                // Invalid format, skip
            }
        }
    }

    /**
     * Split a serialized string into base part and gate part.
     */
    static splitSerialized(serialized: string): { basePart: string; gatePart: string } {
        const pipeIdx = serialized.indexOf('|');
        if (pipeIdx < 0) return { basePart: serialized, gatePart: '' };
        return {
            basePart: serialized.slice(0, pipeIdx),
            gatePart: serialized.slice(pipeIdx + 1),
        };
    }
}
